import {
  useDelete,
  useGetList,
  useNotify,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import useDialogStatus from '../../hooks/useDialogStatus';
import errorNotification from '../../utilities/errorNotification';
import actions, { Actions } from '../../constants/actions';
import hasAccess from '../../utilities/hasAccess';
import { Member } from '../../utilities/schemas/member';
import resourceNames from '../../constants/resourceNames';

const NonAssignedDeleteModalContent = ({
  closeDialog,
  onDelete,
}: {
  closeDialog: () => void;
  onDelete: () => void;
}) => {
  const record = useRecordContext();

  const [deleteOne] = useDelete();
  const notify = useNotify();

  const handleClick = useCallback(() => {
    if (!record) return;
    deleteOne(
      resourceNames.PERMISSION_ROLES,
      {
        id: record.id,
      },
      {
        onSuccess: () => {
          notify('Role deleted', { type: 'info' });
          closeDialog();
          onDelete();
        },
        onError: (e) => {
          notify(errorNotification(e), { type: 'error' });
        },
      },
    );
  }, [deleteOne, notify, onDelete, record, closeDialog]);

  if (!record) return null;

  return (
    <>
      <DialogTitle fontWeight="bold">Delete role</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={6}>
          <Box display="flex" gap={1}>
            <DialogContentText>You are about to delete role:</DialogContentText>
            <DialogContentText fontWeight="bold">{record.name}</DialogContentText>
          </Box>
          <Box display="flex" gap={1}>
            <DialogContentText>Are you sure you want to delete this role?</DialogContentText>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>Cancel</Button>
        <Button onClick={handleClick} variant="contained">Confirm</Button>
      </DialogActions>
    </>
  );
};

const AssignedDeleteModalContent = ({
  closeDialog,
}: {
  closeDialog: () => void;
}) => (
  <>
    <DialogTitle fontWeight="bold">Remove assigned members and API keys</DialogTitle>
    <DialogContent>
      <Box display="flex" flexDirection="column">
        <DialogContentText>
          There are assigned members or API keys to this role.
          To delete a role, first remove all the assigned members and API keys.
        </DialogContentText>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={closeDialog} variant="contained">Okay</Button>
    </DialogActions>
  </>
);

const PermissionRolesDelete = ({
  onDelete,
}: {
  onDelete: () => void;
}) => {
  const { open, openDialog, closeDialog } = useDialogStatus();
  const record = useRecordContext();
  const { permissions } = usePermissions<Actions[]>();

  const { data: member, isLoading: isLoadingMember } = useGetList<Member>(
    'members',
    {
      filter: {
        permissionRoleIds: record?.id,
      },
      pagination: { perPage: 1, page: 1 },
    },
    { enabled: !!record && hasAccess(permissions, actions.MEMBER_LIST) },
  );

  const { data: apiKey, isLoading: isLoadingAPIKey } = useGetList<Member>(
    'api-keys',
    {
      filter: {
        permissionRoleIds: record?.id,
      },
      pagination: { perPage: 1, page: 1 },
    },
    { enabled: !!record && hasAccess(permissions, actions.API_KEY_LIST) },
  );

  const isRoleAssigned = useMemo(
    () => member?.length || apiKey?.length,
    [
      apiKey?.length,
      member?.length,
    ],
  );

  if (!record
    || !hasAccess(permissions, actions.PERMISSION_ROLE_DELETE)
    || isLoadingMember
    || isLoadingAPIKey
  ) return null;

  return (
    <>
      <Dialog open={open} onClose={closeDialog} fullWidth>
        {isRoleAssigned
          ? <AssignedDeleteModalContent closeDialog={closeDialog} />
          : <NonAssignedDeleteModalContent closeDialog={closeDialog} onDelete={onDelete} />}
      </Dialog>
      <Button
        startIcon={<Close />}
        color="error"
        variant="outlined"
        onClick={openDialog}
      >
        Delete
      </Button>
    </>
  );
};

export default PermissionRolesDelete;
